import { CheckCircleIcon, DotsHorizontalIcon, XIcon } from "@heroicons/react/outline";
import { useState } from "react";
import { OAuthError, deleteData, postData, putData, useSettableResource } from "../backend";
import { useModal } from "../contexts";
import { EmailAlias } from "../interfaces";
import SelectField from "./SelectField";
import TextInputField from "./TextInputField";
import ProLabel from "./ProLabel";
import PayWallModalPro from "./PayWallModalPro";
import ErrorModal from "./ErrorModal";
import OAuthModal from "./OAuthModal";
import useUser from "../hooks/useUser";

type SaveState = "INITIAL" | "SAVING" | "SAVED";

function SettingsProfile() {
  const [profile, setProfile] = useSettableResource("/auth/profile/");

  const { openModal } = useModal();
  const { user, refreshUser } = useUser();

  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [saveState, setSaveState] = useState<SaveState>("INITIAL");

  const [aliasName, setAliasName] = useState("");
  const [aliasEmail, setAliasEmail] = useState("");

  const [addingAlias, setAddingAlias] = useState(false);
  const [verifyingAlias, setVerifyingAlias] = useState<string | null>(null);

  async function addAlias() {
    if (!user?.subscription?.featureAliases)
      return openModal(<PayWallModalPro />);

    if (!aliasName || !aliasEmail)
      return;

    setAddingAlias(true);

    try {
      const alias: EmailAlias = await postData("/auth/aliases/", { aliasName, aliasEmail });
      refreshUser();
      verifyAlias(alias.id);

    } catch (error) {
      openModal(<ErrorModal details={error} />);
    }

    setAliasName("");
    setAliasEmail("");
    setAddingAlias(false);
  }

  async function verifyAlias(id: string) {
    setVerifyingAlias(id);

    try {
      await postData(`/auth/aliases/${id}/verify/`);
      setTimeout(() => { refreshUser(); setVerifyingAlias(null); }, 10_000);

    } catch (error) {
      if (error instanceof OAuthError)
        openModal(<OAuthModal oauthUrl={error.oauthUrl} />);
      else
        openModal(<ErrorModal details={error} />);

      setVerifyingAlias(null);
    }
  }

  async function deleteAlias(id: string) {
    await deleteData(`/auth/aliases/${id}/`)
      .then(refreshUser)
      .catch(console.error);
  }

  function updateProfile(field: string, value: string) {
    setHasChanges(true);
    setProfile((user: any) => ({ ...user, profile: { ...user.profile, [field]: value } }));
  }

  function updateUser(field: string, value: string) {
    setHasChanges(true);
    setProfile((user: any) => ({ ...user, [field]: value }));
  }

  function handleSave() {
    setSaveState("SAVING");
    putData("/auth/profile/", profile)
      .then((user) => {
        refreshUser(user);

        setSaveState("SAVED");
        setHasChanges(false);
      });
  }

  let saveIndicator = null;
  if (saveState === "SAVING") {
    saveIndicator = (
      <div className="flex items-center">
        <DotsHorizontalIcon className="h-6 w-6 mr-1 mt-1 text-gray-400" />
        <div className="text-gray-400">Saving</div>
      </div>
    );
  }
  else if (saveState === "SAVED") {
    saveIndicator = (
      <div className="flex items-center">
        <CheckCircleIcon className="h-6 w-6 mr-1 mb-1 text-green" />
        <div className="text-green">Saved</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full p-8">
      <div className="flex items-top justify-between">
        <div className="flex flex-col">
          <div className="font-gilroy font-semibold text-lg">
            Profile
          </div>
          <div className="text-gray-400 text-sm font-semibold">
            Your personal profile will allow to us tailor your experience.
          </div>
        </div>
        <div className="flex items-center font-gilroy font-medium h-10 mb-2">
          <div className="mr-4">
            {saveIndicator}
          </div>
          <button className="btn btn-blue" onClick={handleSave} disabled={saveState === "SAVING" || !hasChanges}>
            Save
          </button>
        </div>
      </div>
      {profile &&
        <div className="overflow-y-auto" style={{ height: "calc(100vh - 200px)" }}>
          <div className="flex mt-10 gap-10">
            <TextInputField className="w-1/2" label="Email" value={profile.email} onChange={() => { }} disabled />
            <SelectField className="w-1/2" label="Timezone" value={profile.timezone || "UTC"} onChange={value => updateUser("timezone", value)}>
              <option>Africa/Abidjan</option>
              <option>Africa/Accra</option>
              <option>Africa/Addis_Ababa</option>
              <option>Africa/Algiers</option>
              <option>Africa/Asmara</option>
              <option>Africa/Bamako</option>
              <option>Africa/Bangui</option>
              <option>Africa/Banjul</option>
              <option>Africa/Bissau</option>
              <option>Africa/Blantyre</option>
              <option>Africa/Brazzaville</option>
              <option>Africa/Bujumbura</option>
              <option>Africa/Cairo</option>
              <option>Africa/Casablanca</option>
              <option>Africa/Ceuta</option>
              <option>Africa/Conakry</option>
              <option>Africa/Dakar</option>
              <option>Africa/Dar_es_Salaam</option>
              <option>Africa/Djibouti</option>
              <option>Africa/Douala</option>
              <option>Africa/El_Aaiun</option>
              <option>Africa/Freetown</option>
              <option>Africa/Gaborone</option>
              <option>Africa/Harare</option>
              <option>Africa/Johannesburg</option>
              <option>Africa/Juba</option>
              <option>Africa/Kampala</option>
              <option>Africa/Khartoum</option>
              <option>Africa/Kigali</option>
              <option>Africa/Kinshasa</option>
              <option>Africa/Lagos</option>
              <option>Africa/Libreville</option>
              <option>Africa/Lome</option>
              <option>Africa/Luanda</option>
              <option>Africa/Lubumbashi</option>
              <option>Africa/Lusaka</option>
              <option>Africa/Malabo</option>
              <option>Africa/Maputo</option>
              <option>Africa/Maseru</option>
              <option>Africa/Mbabane</option>
              <option>Africa/Mogadishu</option>
              <option>Africa/Monrovia</option>
              <option>Africa/Nairobi</option>
              <option>Africa/Ndjamena</option>
              <option>Africa/Niamey</option>
              <option>Africa/Nouakchott</option>
              <option>Africa/Ouagadougou</option>
              <option>Africa/Porto-Novo</option>
              <option>Africa/Sao_Tome</option>
              <option>Africa/Tripoli</option>
              <option>Africa/Tunis</option>
              <option>Africa/Windhoek</option>
              <option>America/Adak</option>
              <option>America/Anchorage</option>
              <option>America/Anguilla</option>
              <option>America/Antigua</option>
              <option>America/Araguaina</option>
              <option>America/Argentina/Buenos_Aires</option>
              <option>America/Argentina/Catamarca</option>
              <option>America/Argentina/Cordoba</option>
              <option>America/Argentina/Jujuy</option>
              <option>America/Argentina/La_Rioja</option>
              <option>America/Argentina/Mendoza</option>
              <option>America/Argentina/Rio_Gallegos</option>
              <option>America/Argentina/Salta</option>
              <option>America/Argentina/San_Juan</option>
              <option>America/Argentina/San_Luis</option>
              <option>America/Argentina/Tucuman</option>
              <option>America/Argentina/Ushuaia</option>
              <option>America/Aruba</option>
              <option>America/Asuncion</option>
              <option>America/Atikokan</option>
              <option>America/Bahia</option>
              <option>America/Bahia_Banderas</option>
              <option>America/Barbados</option>
              <option>America/Belem</option>
              <option>America/Belize</option>
              <option>America/Blanc-Sablon</option>
              <option>America/Boa_Vista</option>
              <option>America/Bogota</option>
              <option>America/Boise</option>
              <option>America/Cambridge_Bay</option>
              <option>America/Campo_Grande</option>
              <option>America/Cancun</option>
              <option>America/Caracas</option>
              <option>America/Cayenne</option>
              <option>America/Cayman</option>
              <option>America/Chicago</option>
              <option>America/Chihuahua</option>
              <option>America/Costa_Rica</option>
              <option>America/Creston</option>
              <option>America/Cuiaba</option>
              <option>America/Curacao</option>
              <option>America/Danmarkshavn</option>
              <option>America/Dawson</option>
              <option>America/Dawson_Creek</option>
              <option>America/Denver</option>
              <option>America/Detroit</option>
              <option>America/Dominica</option>
              <option>America/Edmonton</option>
              <option>America/Eirunepe</option>
              <option>America/El_Salvador</option>
              <option>America/Fort_Nelson</option>
              <option>America/Fortaleza</option>
              <option>America/Glace_Bay</option>
              <option>America/Goose_Bay</option>
              <option>America/Grand_Turk</option>
              <option>America/Grenada</option>
              <option>America/Guadeloupe</option>
              <option>America/Guatemala</option>
              <option>America/Guayaquil</option>
              <option>America/Guyana</option>
              <option>America/Halifax</option>
              <option>America/Havana</option>
              <option>America/Hermosillo</option>
              <option>America/Indiana/Indianapolis</option>
              <option>America/Indiana/Knox</option>
              <option>America/Indiana/Marengo</option>
              <option>America/Indiana/Petersburg</option>
              <option>America/Indiana/Tell_City</option>
              <option>America/Indiana/Vevay</option>
              <option>America/Indiana/Vincennes</option>
              <option>America/Indiana/Winamac</option>
              <option>America/Inuvik</option>
              <option>America/Iqaluit</option>
              <option>America/Jamaica</option>
              <option>America/Juneau</option>
              <option>America/Kentucky/Louisville</option>
              <option>America/Kentucky/Monticello</option>
              <option>America/Kralendijk</option>
              <option>America/La_Paz</option>
              <option>America/Lima</option>
              <option>America/Los_Angeles</option>
              <option>America/Lower_Princes</option>
              <option>America/Maceio</option>
              <option>America/Managua</option>
              <option>America/Manaus</option>
              <option>America/Marigot</option>
              <option>America/Martinique</option>
              <option>America/Matamoros</option>
              <option>America/Mazatlan</option>
              <option>America/Menominee</option>
              <option>America/Merida</option>
              <option>America/Metlakatla</option>
              <option>America/Mexico_City</option>
              <option>America/Miquelon</option>
              <option>America/Moncton</option>
              <option>America/Monterrey</option>
              <option>America/Montevideo</option>
              <option>America/Montserrat</option>
              <option>America/Nassau</option>
              <option>America/New_York</option>
              <option>America/Nipigon</option>
              <option>America/Nome</option>
              <option>America/Noronha</option>
              <option>America/North_Dakota/Beulah</option>
              <option>America/North_Dakota/Center</option>
              <option>America/North_Dakota/New_Salem</option>
              <option>America/Nuuk</option>
              <option>America/Ojinaga</option>
              <option>America/Panama</option>
              <option>America/Pangnirtung</option>
              <option>America/Paramaribo</option>
              <option>America/Phoenix</option>
              <option>America/Port-au-Prince</option>
              <option>America/Port_of_Spain</option>
              <option>America/Porto_Velho</option>
              <option>America/Puerto_Rico</option>
              <option>America/Punta_Arenas</option>
              <option>America/Rainy_River</option>
              <option>America/Rankin_Inlet</option>
              <option>America/Recife</option>
              <option>America/Regina</option>
              <option>America/Resolute</option>
              <option>America/Rio_Branco</option>
              <option>America/Santarem</option>
              <option>America/Santiago</option>
              <option>America/Santo_Domingo</option>
              <option>America/Sao_Paulo</option>
              <option>America/Scoresbysund</option>
              <option>America/Sitka</option>
              <option>America/St_Barthelemy</option>
              <option>America/St_Johns</option>
              <option>America/St_Kitts</option>
              <option>America/St_Lucia</option>
              <option>America/St_Thomas</option>
              <option>America/St_Vincent</option>
              <option>America/Swift_Current</option>
              <option>America/Tegucigalpa</option>
              <option>America/Thule</option>
              <option>America/Thunder_Bay</option>
              <option>America/Tijuana</option>
              <option>America/Toronto</option>
              <option>America/Tortola</option>
              <option>America/Vancouver</option>
              <option>America/Whitehorse</option>
              <option>America/Winnipeg</option>
              <option>America/Yakutat</option>
              <option>America/Yellowknife</option>
              <option>Antarctica/Casey</option>
              <option>Antarctica/Davis</option>
              <option>Antarctica/DumontDUrville</option>
              <option>Antarctica/Macquarie</option>
              <option>Antarctica/Mawson</option>
              <option>Antarctica/McMurdo</option>
              <option>Antarctica/Palmer</option>
              <option>Antarctica/Rothera</option>
              <option>Antarctica/Syowa</option>
              <option>Antarctica/Troll</option>
              <option>Antarctica/Vostok</option>
              <option>Arctic/Longyearbyen</option>
              <option>Asia/Aden</option>
              <option>Asia/Almaty</option>
              <option>Asia/Amman</option>
              <option>Asia/Anadyr</option>
              <option>Asia/Aqtau</option>
              <option>Asia/Aqtobe</option>
              <option>Asia/Ashgabat</option>
              <option>Asia/Atyrau</option>
              <option>Asia/Baghdad</option>
              <option>Asia/Bahrain</option>
              <option>Asia/Baku</option>
              <option>Asia/Bangkok</option>
              <option>Asia/Barnaul</option>
              <option>Asia/Beirut</option>
              <option>Asia/Bishkek</option>
              <option>Asia/Brunei</option>
              <option>Asia/Chita</option>
              <option>Asia/Choibalsan</option>
              <option>Asia/Colombo</option>
              <option>Asia/Damascus</option>
              <option>Asia/Dhaka</option>
              <option>Asia/Dili</option>
              <option>Asia/Dubai</option>
              <option>Asia/Dushanbe</option>
              <option>Asia/Famagusta</option>
              <option>Asia/Gaza</option>
              <option>Asia/Hebron</option>
              <option>Asia/Ho_Chi_Minh</option>
              <option>Asia/Hong_Kong</option>
              <option>Asia/Hovd</option>
              <option>Asia/Irkutsk</option>
              <option>Asia/Jakarta</option>
              <option>Asia/Jayapura</option>
              <option>Asia/Jerusalem</option>
              <option>Asia/Kabul</option>
              <option>Asia/Kamchatka</option>
              <option>Asia/Karachi</option>
              <option>Asia/Kathmandu</option>
              <option>Asia/Khandyga</option>
              <option>Asia/Kolkata</option>
              <option>Asia/Krasnoyarsk</option>
              <option>Asia/Kuala_Lumpur</option>
              <option>Asia/Kuching</option>
              <option>Asia/Kuwait</option>
              <option>Asia/Macau</option>
              <option>Asia/Magadan</option>
              <option>Asia/Makassar</option>
              <option>Asia/Manila</option>
              <option>Asia/Muscat</option>
              <option>Asia/Nicosia</option>
              <option>Asia/Novokuznetsk</option>
              <option>Asia/Novosibirsk</option>
              <option>Asia/Omsk</option>
              <option>Asia/Oral</option>
              <option>Asia/Phnom_Penh</option>
              <option>Asia/Pontianak</option>
              <option>Asia/Pyongyang</option>
              <option>Asia/Qatar</option>
              <option>Asia/Qostanay</option>
              <option>Asia/Qyzylorda</option>
              <option>Asia/Riyadh</option>
              <option>Asia/Sakhalin</option>
              <option>Asia/Samarkand</option>
              <option>Asia/Seoul</option>
              <option>Asia/Shanghai</option>
              <option>Asia/Singapore</option>
              <option>Asia/Srednekolymsk</option>
              <option>Asia/Taipei</option>
              <option>Asia/Tashkent</option>
              <option>Asia/Tbilisi</option>
              <option>Asia/Tehran</option>
              <option>Asia/Thimphu</option>
              <option>Asia/Tokyo</option>
              <option>Asia/Tomsk</option>
              <option>Asia/Ulaanbaatar</option>
              <option>Asia/Urumqi</option>
              <option>Asia/Ust-Nera</option>
              <option>Asia/Vientiane</option>
              <option>Asia/Vladivostok</option>
              <option>Asia/Yakutsk</option>
              <option>Asia/Yangon</option>
              <option>Asia/Yekaterinburg</option>
              <option>Asia/Yerevan</option>
              <option>Atlantic/Azores</option>
              <option>Atlantic/Bermuda</option>
              <option>Atlantic/Canary</option>
              <option>Atlantic/Cape_Verde</option>
              <option>Atlantic/Faroe</option>
              <option>Atlantic/Madeira</option>
              <option>Atlantic/Reykjavik</option>
              <option>Atlantic/South_Georgia</option>
              <option>Atlantic/St_Helena</option>
              <option>Atlantic/Stanley</option>
              <option>Australia/Adelaide</option>
              <option>Australia/Brisbane</option>
              <option>Australia/Broken_Hill</option>
              <option>Australia/Darwin</option>
              <option>Australia/Eucla</option>
              <option>Australia/Hobart</option>
              <option>Australia/Lindeman</option>
              <option>Australia/Lord_Howe</option>
              <option>Australia/Melbourne</option>
              <option>Australia/Perth</option>
              <option>Australia/Sydney</option>
              <option>Canada/Atlantic</option>
              <option>Canada/Central</option>
              <option>Canada/Eastern</option>
              <option>Canada/Mountain</option>
              <option>Canada/Newfoundland</option>
              <option>Canada/Pacific</option>
              <option>Europe/Amsterdam</option>
              <option>Europe/Andorra</option>
              <option>Europe/Astrakhan</option>
              <option>Europe/Athens</option>
              <option>Europe/Belgrade</option>
              <option>Europe/Berlin</option>
              <option>Europe/Bratislava</option>
              <option>Europe/Brussels</option>
              <option>Europe/Bucharest</option>
              <option>Europe/Budapest</option>
              <option>Europe/Busingen</option>
              <option>Europe/Chisinau</option>
              <option>Europe/Copenhagen</option>
              <option>Europe/Dublin</option>
              <option>Europe/Gibraltar</option>
              <option>Europe/Guernsey</option>
              <option>Europe/Helsinki</option>
              <option>Europe/Isle_of_Man</option>
              <option>Europe/Istanbul</option>
              <option>Europe/Jersey</option>
              <option>Europe/Kaliningrad</option>
              <option>Europe/Kiev</option>
              <option>Europe/Kirov</option>
              <option>Europe/Lisbon</option>
              <option>Europe/Ljubljana</option>
              <option>Europe/London</option>
              <option>Europe/Luxembourg</option>
              <option>Europe/Madrid</option>
              <option>Europe/Malta</option>
              <option>Europe/Mariehamn</option>
              <option>Europe/Minsk</option>
              <option>Europe/Monaco</option>
              <option>Europe/Moscow</option>
              <option>Europe/Oslo</option>
              <option>Europe/Paris</option>
              <option>Europe/Podgorica</option>
              <option>Europe/Prague</option>
              <option>Europe/Riga</option>
              <option>Europe/Rome</option>
              <option>Europe/Samara</option>
              <option>Europe/San_Marino</option>
              <option>Europe/Sarajevo</option>
              <option>Europe/Saratov</option>
              <option>Europe/Simferopol</option>
              <option>Europe/Skopje</option>
              <option>Europe/Sofia</option>
              <option>Europe/Stockholm</option>
              <option>Europe/Tallinn</option>
              <option>Europe/Tirane</option>
              <option>Europe/Ulyanovsk</option>
              <option>Europe/Uzhgorod</option>
              <option>Europe/Vaduz</option>
              <option>Europe/Vatican</option>
              <option>Europe/Vienna</option>
              <option>Europe/Vilnius</option>
              <option>Europe/Volgograd</option>
              <option>Europe/Warsaw</option>
              <option>Europe/Zagreb</option>
              <option>Europe/Zaporozhye</option>
              <option>Europe/Zurich</option>
              <option>GMT</option>
              <option>Indian/Antananarivo</option>
              <option>Indian/Chagos</option>
              <option>Indian/Christmas</option>
              <option>Indian/Cocos</option>
              <option>Indian/Comoro</option>
              <option>Indian/Kerguelen</option>
              <option>Indian/Mahe</option>
              <option>Indian/Maldives</option>
              <option>Indian/Mauritius</option>
              <option>Indian/Mayotte</option>
              <option>Indian/Reunion</option>
              <option>Pacific/Apia</option>
              <option>Pacific/Auckland</option>
              <option>Pacific/Bougainville</option>
              <option>Pacific/Chatham</option>
              <option>Pacific/Chuuk</option>
              <option>Pacific/Easter</option>
              <option>Pacific/Efate</option>
              <option>Pacific/Enderbury</option>
              <option>Pacific/Fakaofo</option>
              <option>Pacific/Fiji</option>
              <option>Pacific/Funafuti</option>
              <option>Pacific/Galapagos</option>
              <option>Pacific/Gambier</option>
              <option>Pacific/Guadalcanal</option>
              <option>Pacific/Guam</option>
              <option>Pacific/Honolulu</option>
              <option>Pacific/Kiritimati</option>
              <option>Pacific/Kosrae</option>
              <option>Pacific/Kwajalein</option>
              <option>Pacific/Majuro</option>
              <option>Pacific/Marquesas</option>
              <option>Pacific/Midway</option>
              <option>Pacific/Nauru</option>
              <option>Pacific/Niue</option>
              <option>Pacific/Norfolk</option>
              <option>Pacific/Noumea</option>
              <option>Pacific/Pago_Pago</option>
              <option>Pacific/Palau</option>
              <option>Pacific/Pitcairn</option>
              <option>Pacific/Pohnpei</option>
              <option>Pacific/Port_Moresby</option>
              <option>Pacific/Rarotonga</option>
              <option>Pacific/Saipan</option>
              <option>Pacific/Tahiti</option>
              <option>Pacific/Tarawa</option>
              <option>Pacific/Tongatapu</option>
              <option>Pacific/Wake</option>
              <option>Pacific/Wallis</option>
              <option>US/Alaska</option>
              <option>US/Arizona</option>
              <option>US/Central</option>
              <option>US/Eastern</option>
              <option>US/Hawaii</option>
              <option>US/Mountain</option>
              <option>US/Pacific</option>
              <option>UTC</option>
            </SelectField>
          </div>
          <div className="flex gap-10 mt-5">
            <TextInputField className="w-full" label="First Name" value={profile.firstName} onChange={() => { }} disabled />
            <TextInputField className="w-full" label="Last Name" value={profile.lastName} onChange={() => { }} disabled />
          </div>
          <div className="flex gap-10 mt-5">
            <TextInputField className="w-full" label="Job Title" value={profile.profile?.jobTitle || ""} onChange={value => updateProfile("jobTitle", value)} />
            <SelectField className="w-full" label="Industry" value={profile.profile?.industry || "null"} onChange={value => updateProfile("industry", value)}>
              <option disabled value="null">Select an option</option>
              <option>Accounting</option>
              <option>Administration &amp; Office Support</option>
              <option>Advertising, Arts &amp; Media</option>
              <option>Banking &amp; Financial Services</option>
              <option>Call Centre &amp; Customer Service</option>
              <option>Community Services &amp; Development</option>
              <option>Construction</option>
              <option>Consulting &amp; Strategy</option>
              <option>Design &amp; Architecture</option>
              <option>Education &amp; Training</option>
              <option>Engineering</option>
              <option>Farming, Animals &amp; Conservation</option>
              <option>Government &amp; Defence</option>
              <option>Healthcare &amp; Medical</option>
              <option>Hospitality &amp; Tourism</option>
              <option>Human Resources &amp; Recruitment</option>
              <option>Information &amp; Communication Technology</option>
              <option>Insurance</option>
              <option>Internet</option>
              <option>Legal</option>
              <option>Manufacturing, Transport &amp; Logistics</option>
              <option>Marketing &amp; Communications</option>
              <option>Mining, Resources &amp; Energy</option>
              <option>Real Estate &amp; Property</option>
              <option>Retail &amp; Consumer Products</option>
              <option>Sales</option>
              <option>Science &amp; Technology</option>
              <option>Sport &amp; Recreation</option>
              <option>Trades &amp; Services</option>
              <option>Other</option>
            </SelectField>
          </div>
          <div className="flex gap-10 mt-5">
            <TextInputField className="w-full" label="Company" value={profile.profile.companyName || ""} onChange={value => updateProfile("companyName", value)} />
            <SelectField className="w-full" label="Company Size" value={profile.profile.companySize || "null"} onChange={value => updateProfile("companySize", value)}>
              <option disabled value="null">Select an option</option>
              <option value="1">Self-employed</option>
              <option value="1-10">1 - 10 employees</option>
              <option value="11-50">11 - 50 employees</option>
              <option value="51-200">51 - 200 employees</option>
              <option value="201-500">201 - 500 employees</option>
              <option value="501-1000">501 - 1000 employees</option>
              <option value="1001-5000">1001 - 5000 employees</option>
              <option value="5001-10000">5000 - 10,000 employees</option>
              <option value="10001+">10,001+ employees</option>
            </SelectField>
          </div>

          <div className="flex flex-col mt-8">
            <div className="flex items-center font-gilroy font-semibold text-lg text-gray-600 w-44">
              <div className="-mb-1">Email Aliases</div> <ProLabel />
            </div>
            <div className="text-sm text-gray-400 font-gilroy font-semibold mt-1">
              Send emails and signature requests from any of your email aliases configured in Gmail
            </div>

            {user!.aliases.map(alias =>
              <div className="flex items-center gap-2 mt-2 h-8">
                <div className="font-semibold w-96">
                  {alias.aliasName} {"<"}{alias.aliasEmail}{">"}
                </div>
                {alias.verified &&
                  <div className="flex items-center justify-center gap-1 text-green font-semibold w-32">
                    Verified <CheckCircleIcon className="w-6 h-6 -mt-1" />
                  </div>
                }
                {(!alias.verified && verifyingAlias !== alias.id) &&
                  <button className="btn btn-white text-sm py-1 w-32" onClick={() => verifyAlias(alias.id)}>Verify</button>
                }
                {(!alias.verified && verifyingAlias === alias.id) &&
                  <div className="flex items-center justify-center text-gray-400 font-semibold w-32">
                    Verifying<span className="loading-ellipse w-5" />
                  </div>
                }
                <XIcon className="w-6 h-6 ml-4 hover:text-red text-gray-400 cursor-pointer" onClick={() => deleteAlias(alias.id)} />
              </div>
            )}
            <div className="flex items-end gap-2 mt-2">
              <TextInputField className="w-80" label="Name" value={aliasName} onChange={setAliasName} />
              <TextInputField className="w-80" label="Email" value={aliasEmail} onChange={setAliasEmail} />
              <button className="btn btn-blue text-sm w-32" onClick={addAlias} disabled={addingAlias}>{addingAlias ? "Adding..." : "Add"}</button>
            </div>
            <div className="text-sm text-gray-600 mt-1">
              Before adding a new alias in Portant, ensure it is registered in your Gmail settings. <a className="text-blue underline" href="https://support.google.com/mail/answer/22370?hl=en" target="_blank" rel="noreferrer">Learn more</a>
            </div>
          </div>
        </div>
      }
    </div>
  );
}

export default SettingsProfile;
